import { Component, effect, EventEmitter, Output, ViewChild } from '@angular/core';
import { StaffApiService } from '../../../core/api/services';
import { PatientSessionService } from '../patient-session.service';
import { DataTakerStaffDataModel, PatientSessionModel } from '../../../core/api/models';
import { AuthService } from '../../../auth/auth.service';
import { Router } from '@angular/router';
import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from '../../../shared/components/signature-pad/signature-pad.component';
import { AlertService } from '../../../shared/services/alert.service';
import { dataURItoBlob } from '../../../shared/utils/utils';
import { OfflineDataService } from '../../../core/services/offline-data.service';
@Component({
  selector: 'app-end-session',
  templateUrl: './end-session.component.html',
  styleUrl: './end-session.component.css',
  standalone: false,
})
export class EndSessionComponent {
  @Output() close = new EventEmitter<void>();
  @Output() finished = new EventEmitter<void>();

  handoff: 'staff' | 'guardian' | 'temporary' = 'staff';
  guardian: any;
  selectedStaff: { StaffID: number; FullName: string } | null = null;
  availableStaff: { StaffID: number; FullName: string }[] = [];
  explain: string = '';
  handoffOptions = [
    { label: 'Hand off to Staff', value: 'staff' },
    { label: 'Hand off to Guardian', value: 'guardian' },
    { label: 'Temporary Handoff', value: 'temporary' },
  ];
  handoffTips: Record<'staff' | 'temporary' | 'guardian', string> = {
    staff:
      'Use this when permanently handing responsibility to another staff member.',
    temporary:
      'Use this for short-term coverage before guardian pickup or temporary supervision.',
    guardian:
      'Use this when the patient is being released directly to a guardian.',
  };
  step:
    | 'start'
    | 'staffSelectAndSign'
    | 'temporarySelectAndSign'
    | 'nextGuardian'
    | 'NoOnGuardianHandoff'
    | 'YesOnGuardianHandoff'
    | 'afterGuardianName' = 'start';
  guardians: any[] = [{ Name: 'Other', EFI_LiablePersonID: null }];

  patientSession: PatientSessionModel | null = null;
  otherGuardianName: string = '';
  @ViewChild(SignaturePadComponent, { static: false }) signaturePad!: SignaturePadComponent;


  public signaturePadOptions: NgSignaturePadOptions = { 
    canvasHeight: 350,
    canvasWidth: 1250,

  };


  constructor(
    private staffApiService: StaffApiService,
    private patientSessionService: PatientSessionService,
    private authService: AuthService,
    private router: Router,
    private alertService: AlertService,
    private offlineDataService: OfflineDataService
  ) {
    effect(() => {
      this.patientSession = this.patientSessionService.getPatientSession();

      if (this.patientSession) {
        this.initializeStaffSelection();
        this.explain = this.patientSession.Explanation ?? '';
        this.staffApiService.GetPatientGuardians({ patientID: this.patientSession.PatientID }).subscribe(guardians => {
          this.guardians = guardians;
          this.guardians.push({ Name: 'Other', EFI_LiablePersonID: null });
        });
      }
    });
  }

  onCancel() {
    this.close.emit();
  }

  get selectedHandoffTip(): string {
    return this.handoffTips[this.handoff];
  }

  onSubmit() {
    if (
      this.step == 'staffSelectAndSign' ||
      this.step == 'temporarySelectAndSign'
    ) {
      if (!this.selectedStaff) {
        this.alertService.error('Please select the next responsible staff');
        return;
      }

      const signatureData = this.signaturePad?.toDataURL();
      const signatureBlob = dataURItoBlob(signatureData);
      if (!signatureBlob || signatureBlob.size <= 0) {
        this.alertService.error('Please provide the staff signature');
        return;
      }

      this.patientSession!.Explanation = this.explain;
      this.patientSession!.HandoffStaffID =
        this.step === 'staffSelectAndSign' ? this.selectedStaff.StaffID : null;
      this.patientSession!.HandoffStaffName =
        this.step === 'staffSelectAndSign' ? this.selectedStaff.FullName : null;
      this.patientSession!.IsTemporaryHandoff =
        this.step === 'temporarySelectAndSign';
      this.patientSession!.TemporaryHandoffStaffID =
        this.step === 'temporarySelectAndSign' ? this.selectedStaff.StaffID : null;
      this.patientSession!.TemporaryHandoffStaffName =
        this.step === 'temporarySelectAndSign'
          ? this.selectedStaff.FullName
          : null;
      this.patientSession!.TemporaryHandoffStartedDate =
        this.step === 'temporarySelectAndSign'
          ? new Date().toServerString()
          : null;
      this.patientSession!.TemporaryHandoffEndedDate = null;
      this.patientSessionService.setPatientSessionStaffHandoffSignature(
        this.patientSession!.ID ?? 0,
        signatureBlob
      );
    }

    if (this.step == 'NoOnGuardianHandoff') {
      this.patientSession!.Explanation = this.explain;
      this.patientSession!.HandoffStaffID = null;
      this.patientSession!.HandoffStaffName = null;
      this.patientSession!.IsTemporaryHandoff = false;
      this.patientSession!.TemporaryHandoffStaffID = null;
      this.patientSession!.TemporaryHandoffStaffName = null;
      this.patientSession!.TemporaryHandoffStartedDate = null;
      this.patientSession!.TemporaryHandoffEndedDate = null;
    }
    if (this.step == 'afterGuardianName') {
      if (this.guardian.Name == 'Other') {
        this.patientSession!.OtherGuardianName = this.otherGuardianName;
        this.patientSession!.EFI_LiablePersonID = null;
      } else {
        this.patientSession!.EFI_LiablePersonID = this.guardian.EFI_LiablePersonID;
        this.patientSession!.OtherGuardianName = '';
      }

      const guardianSignature = this.signaturePad.toDataURL();
      const guardianSignatureBlob = dataURItoBlob(guardianSignature);
      if (this.patientSession?.ID && guardianSignatureBlob && guardianSignatureBlob.size > 0 && this.patientSession?.ID >= 0) {
        this.patientSessionService.setPatientSessionGuardianSignature(this.patientSession?.ID, guardianSignatureBlob);
      }
      this.patientSession!.Explanation = this.explain;
      this.patientSession!.HandoffStaffID = null;
      this.patientSession!.HandoffStaffName = null;
      this.patientSession!.IsTemporaryHandoff = false;
      this.patientSession!.TemporaryHandoffStaffID = null;
      this.patientSession!.TemporaryHandoffStaffName = null;
      this.patientSession!.TemporaryHandoffStartedDate = null;
      this.patientSession!.TemporaryHandoffEndedDate = null;
    }

    this.finished.emit();
    const serviceID = this.patientSession!.ServiceID;
    const patientID = this.patientSession!.PatientID;
    this.patientSessionService.endSession(this.patientSession!).subscribe(() => {
      this.close.emit();
      if (this.offlineDataService.isInOfflineMode()) {
        this.alertService.success('Session ended successfully. Your data is saved offline, remember to access Spark when you\'re online to get it uploaded');
        this.router.navigate(['/offline/patients']);
      } else {
        if (serviceID) {
          this.alertService.confirm('Session ended successfully', 'Would you like to go back to Opto to create the documentation?').subscribe(result => {
            if (result) {
              window.open(this.authService.getOptoURL() ? (this.authService.getOptoURL() + '/TreatmentReports/Documentation/CreateSparkDoc/' + serviceID + '/' + patientID) : '/');
              setTimeout(() => {
                this.router.navigate(['/staff/' + patientID + '/sessions']);
              }, 1000);
            } else {
              this.alertService.success('Session ended successfully');
              setTimeout(() => {
                this.router.navigate(['/staff/' + patientID + '/sessions']);
              }, 1000);
            }
          }, error => {
            this.alertService.success('Session ended successfully');
            setTimeout(() => {
              this.router.navigate(['/staff/' + patientID + '/sessions']);
            }, 1000);
          });
        } else {
          this.alertService.success('Session ended successfully');
          setTimeout(() => {
            this.router.navigate(['/staff/' + patientID + '/sessions']);
          }, 1000);
        }
      }
    });
  }

  onNextOnHandoff() {
    if (this.handoff == 'staff') {
      this.step = 'staffSelectAndSign';
      this.patientSession!.IsHandoffToStaff = true;
      this.patientSession!.IsHandoffToParent = false;
      this.patientSession!.HandoffStaffID = null;
      this.patientSession!.HandoffStaffName = null;
      this.patientSession!.IsTemporaryHandoff = false;
      this.patientSession!.TemporaryHandoffStaffID = null;
      this.patientSession!.TemporaryHandoffStaffName = null;
      this.patientSession!.TemporaryHandoffStartedDate = null;
      this.patientSession!.TemporaryHandoffEndedDate = null;
      this.saveSession();
      return;
    }

    if (this.handoff == 'temporary') {
      this.step = 'temporarySelectAndSign';
      this.patientSession!.IsHandoffToStaff = true;
      this.patientSession!.IsHandoffToParent = false;
      this.patientSession!.HandoffStaffID = null;
      this.patientSession!.HandoffStaffName = null;
      this.saveSession();
      return;
    }

    if (this.handoff == 'guardian') {
      this.step = 'nextGuardian';
      this.patientSession!.IsHandoffToStaff = false;
      this.patientSession!.IsHandoffToParent = true;
      this.patientSession!.HandoffStaffID = null;
      this.patientSession!.HandoffStaffName = null;
      this.patientSession!.IsTemporaryHandoff = false;
      this.patientSession!.TemporaryHandoffStaffID = null;
      this.patientSession!.TemporaryHandoffStaffName = null;
      this.patientSession!.TemporaryHandoffStartedDate = null;
      this.patientSession!.TemporaryHandoffEndedDate = null;
    }
    this.saveSession();
  }

  onNoOnGuardianHandoff() {
    this.step = 'NoOnGuardianHandoff';
  }

  onYesOnGuardianHandoff() {
    this.step = 'YesOnGuardianHandoff';
  }

  onAfterGuardianName() {
    if (!this.guardian && !this.otherGuardianName) {
      this.alertService.error('Please select a guardian or select "Other" and enter a name');
      return;
    }
    this.step = 'afterGuardianName';
    this.saveSession();
  }

  saveSession() {
    this.patientSessionService.setPatientSession(Object.assign({}, this.patientSession), true);
  }

  private initializeStaffSelection() {
    if (!this.patientSession) {
      this.availableStaff = [];
      this.selectedStaff = null;
      return;
    }

    const fallbackStaff = this.buildFallbackAvailableStaff();

    this.staffApiService.GetActiveStaffMembers().subscribe({
      next: (staffList: DataTakerStaffDataModel[]) => {
        const apiStaff = (staffList ?? [])
          .filter((staff) => !!staff.StaffID)
          .map((staff) => ({
            StaffID: staff.StaffID as number,
            FullName: (staff.FullName ?? '').trim() || `Staff #${staff.StaffID}`,
          }));
        const mergedMap = new Map<number, { StaffID: number; FullName: string }>();
        [...apiStaff, ...fallbackStaff].forEach((staff) =>
          mergedMap.set(staff.StaffID, staff)
        );
        this.availableStaff = Array.from(mergedMap.values()).sort((a, b) =>
          a.FullName.localeCompare(b.FullName)
        );
        this.setDefaultSelectedStaff();
      },
      error: () => {
        this.availableStaff = [...fallbackStaff].sort((a, b) =>
          a.FullName.localeCompare(b.FullName)
        );
        this.setDefaultSelectedStaff();
      },
    });
  }

  private buildFallbackAvailableStaff(): { StaffID: number; FullName: string }[] {
    if (!this.patientSession) {
      return [];
    }

    const availableStaffMap = new Map<number, { StaffID: number; FullName: string }>();
    const staffIDs = this.patientSession.StaffIDs ?? [];
    const staffNames = this.patientSession.StaffNames ?? [];

    staffIDs.forEach((staffID, index) => {
      const name =
        staffNames[index] ?? this.patientSession?.StaffName ?? `Staff #${staffID}`;
      availableStaffMap.set(staffID, { StaffID: staffID, FullName: name });
    });

    if (this.patientSession.StaffID) {
      availableStaffMap.set(this.patientSession.StaffID, {
        StaffID: this.patientSession.StaffID,
        FullName:
          this.patientSession.StaffName ??
          availableStaffMap.get(this.patientSession.StaffID)?.FullName ??
          `Staff #${this.patientSession.StaffID}`,
      });
    }

    if (this.authService.userInfo?.ID) {
      availableStaffMap.set(this.authService.userInfo.ID, {
        StaffID: this.authService.userInfo.ID,
        FullName: this.authService.userInfo.Fullnane ?? 'Current Staff',
      });
    }

    return Array.from(availableStaffMap.values());
  }

  private setDefaultSelectedStaff() {
    if (!this.availableStaff.length) {
      this.selectedStaff = null;
      return;
    }

    if (this.patientSession?.TemporaryHandoffStaffID) {
      this.selectedStaff =
        this.availableStaff.find(
          (staff) =>
            staff.StaffID === this.patientSession?.TemporaryHandoffStaffID
        ) ?? this.availableStaff[0];
      return;
    }

    if (this.patientSession?.HandoffStaffID) {
      this.selectedStaff =
        this.availableStaff.find(
          (staff) => staff.StaffID === this.patientSession?.HandoffStaffID
        ) ?? this.availableStaff[0];
      return;
    }

    this.selectedStaff =
      this.availableStaff.find(
        (staff) => staff.StaffID === this.patientSession?.StaffID
      ) ??
      this.availableStaff.find(
        (staff) => staff.StaffID === this.authService.userInfo?.ID
      ) ??
      this.availableStaff[0];
  }
}
